/**
 * The one festival page's vocabulary: the status labels, the step and choice labels, the day formats and the sentence
 * for each problem key Tina answers. `@/types/tina-festival-page` owns the wire shapes; the 409 sentences are
 * `festivals.ts`'s `conflictText`.
 *
 * The preview lines, the suggestion texts and `status_label` are Tina's `text`, shown as they come: IMS keeps no copy
 * of them. Every function degrades instead of throwing: a problem key this build does not know comes back raw.
 */
import { MONTHS, dayRange, parseDay } from "@/lib/tina/festivals";
import {
  type ExistingChoiceKind,
  FESTIVAL_STATUSES,
  type FestivalStatus,
  type PublishStepState,
} from "@/types/tina-festival-page";

/** The nine statuses as Plan 7 spells them (`·` is U+00B7 with a space each side). */
export const FESTIVAL_STATUS_LABEL: Record<FestivalStatus, string> = {
  waiting_approval: "Waiting for approval",
  waiting_venue: "Waiting · venue missing",
  group_waiting: "Group waiting",
  publishing: "Publishing",
  live: "Live",
  live_changes: "Live · changes waiting",
  failed: "Failed · see page",
  rejected: "Rejected",
  ended_unapproved: "Ended · not approved",
};

/** What the list shows when no status is asked for: every status a curator has to act on (Tina's own set). */
export const NEEDS_A_PERSON: readonly FestivalStatus[] = [
  "waiting_approval",
  "waiting_venue",
  "group_waiting",
  "live_changes",
  "failed",
];

export function isFestivalStatus(value: unknown): value is FestivalStatus {
  return FESTIVAL_STATUSES.some((status) => status === value);
}

export const PUBLISH_STEP_STATE_LABEL: Record<PublishStepState, string> = {
  pending: "Waiting",
  running: "Working on it",
  done: "Done",
  needs_you: "Needs you",
  failed: "Failed",
  skipped: "Skipped",
};

/** A choice for an event already in Mood; `dayNumber` is the day it becomes, for "Use as Day 2". */
export function existingChoiceLabel(
  choice: ExistingChoiceKind,
  dayNumber: number | null,
): string {
  switch (choice) {
    case "replace":
      return "Replace with the day events";
    case "use_as_day":
      return dayNumber === null ? "Use as a day" : `Use as Day ${dayNumber}`;
    case "not_part":
      return "Not part of the festival";
  }
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/** `Fri 11 Dec`: the weekday is read from the date's own parts, never from the local time zone. */
export function shortDay(day: string): string {
  const parts = parseDay(day);
  if (parts === null) return day;
  const weekday = new Date(
    Date.UTC(parts.year, parts.month - 1, parts.day),
  ).getUTCDay();
  return `${WEEKDAYS[weekday]} ${parts.day} ${MONTHS[parts.month - 1]}`;
}

/** `Fri 11 Dec 2026`. */
export function longDay(day: string): string {
  const parts = parseDay(day);
  return parts === null ? day : `${shortDay(day)} ${parts.year}`;
}

type ProblemSentence = (
  value: string,
  nameOf: (id: string) => string,
) => string;

const onDay = (value: string): string => dayRange(value, value) || value;

const PROBLEM_TEXT: Record<string, ProblemSentence> = {
  blank_name: () =>
    "Give the festival a name with at least one letter or digit.",
  no_days: () => "Add at least one day.",
  venue_missing: (value) => `Pick a venue for ${onDay(value)}.`,
  blank_venue: (value) => `Pick a venue for ${onDay(value)}.`,
  duplicate_day: (value) =>
    `${onDay(value)} is on the page twice. Keep one box per day.`,
  two_events_for_day: (value) =>
    `Two events are used as the event of ${onDay(value)}. Use only one.`,
  choice_day_missing: (value, nameOf) =>
    `Say which day "${nameOf(value)}" belongs to.`,
  choice_day_unknown: (value, nameOf) =>
    `Say which day "${nameOf(value)}" belongs to.`,
  used_on_another_day: (value, nameOf) =>
    `"${nameOf(value)}" is already used as the event of another day.`,
  unknown_line: (value, nameOf) =>
    `"${nameOf(value)}" is no longer listed. Reload the page.`,
  unknown_page: (value) =>
    `The page ${value} is not this festival's. Reload the page.`,
  page_of_another_record: (value) =>
    `The page ${value} is not this festival's. Reload the page.`,
  two_own_pages: () => "Mark only one page as the festival's own.",
  own_page_not_member: (value) =>
    `The festival's own page ${value} must stay part of the festival.`,
  empty_alias: (value) =>
    `"${value}" is not a usable name: nothing is left once years and words like festival are removed.`,
  no_poster: () => "No page Tina read has a poster. Paste an image URL.",
  bad_poster_url: () => "The poster is not a web address.",
  bad_set_time: (value) =>
    `A set time on ${onDay(value)} is not a time like 21:30.`,
  unknown_from_day: () => DAYS_CHANGED,
  from_day_twice: () => DAYS_CHANGED,
  stored_day_unaccounted: () => DAYS_CHANGED,
  removed_not_stored: () => DAYS_CHANGED,
  move_cycle: () =>
    "Two days swap dates. Move one, publish, then move the other.",
  use_as_day_taken: (value) =>
    `${onDay(value)} already has its event in Mood. Choose Replace or Not part.`,
  artist_required: () => "Pick the artist first.",
  choice_not_for_suggestion: () => "That choice does not fit this suggestion.",
};

const DAYS_CHANGED = "The days changed while you worked. Reload the page.";

/**
 * One sentence for a problem key of a 422 or of the preview's `problems`. Split on the FIRST colon (a url carries
 * colons of its own). A key this build does not know comes back as the raw string. `nameOf` turns a Mood event id
 * into the name a curator reads.
 */
export function pageProblemText(
  problem: string,
  nameOf: (moodEventId: string) => string = (id) => id,
): string {
  const colon = problem.indexOf(":");
  const key = colon === -1 ? problem : problem.slice(0, colon);
  if (!Object.hasOwn(PROBLEM_TEXT, key)) return problem;
  const value = colon === -1 ? "" : problem.slice(colon + 1);
  return PROBLEM_TEXT[key](value, nameOf);
}
